import type { SpecPolicyFlagSet } from "@automatry/schemas";
import {
  ENGINE_SAFE_TAKEOFF_RAW_FIELDS_KEY,
  EngineSafeTakeoffProjectionSchema,
  projectTakeoffForEngine
} from "@automatry/schemas";

import type { SpecifyProjectInput } from "../../app/specifyProject";
import { normaliseApartmentTypeKey } from "./apartmentDedup";

export type TakeoffRow =
  SpecifyProjectInput["apartmentTypes"][number]["takeoffRows"][number];
export type Requirement =
  SpecifyProjectInput["apartmentTypes"][number]["requirements"][number];
export type SourceRef = TakeoffRow["sourceRefs"][number];

export interface CreateProjectRequirementDraft {
  centralControlScreen: boolean;
  leakDetection: boolean;
  heatMetering: boolean;
  bmsIntegration: boolean;
}

export type DraftProvenance = "source" | "manual";
export type RequirementToggleProvenance = Partial<
  Record<keyof CreateProjectRequirementDraft, DraftProvenance>
>;

export interface CreateProjectRequirementTextDraft {
  id: string;
  type: string;
  interpretedText: string;
  priority: Requirement["priority"];
  confidence: string;
  scopeLabel: string;
  uiStrategy?: Requirement["uiStrategy"];
  edgeFeature?: Requirement["edgeFeature"];
  provenance?: DraftProvenance;
  sourceRefs?: SourceRef[];
  sourceRequirementRowId?: string;
  sourceNoteId?: string;
  sourceNoteLabel?: string;
  sourceNoteAuthorType?: "user" | "ai";
}

export interface CreateProjectApartmentTypeDraft {
  id: string;
  name: string;
  homes: string;
  beds: string;
  fcuZones: string;
  ufhZones: string;
  wetActuatorCount?: string;
  eufhZones: string;
  towelRailCount: string;
  standaloneHeatOnlyZones: string;
  thermostatUiPoints: string;
  centralScreens: string;
  hiuCount: string;
  ciuCount: string;
  althermaCount: string;
  provenance?: DraftProvenance;
}

export interface CreateProjectApartmentSourceMetadata {
  sourceRefs?: SourceRef[];
  sourceTakeoffArtifactId?: string;
  sourceRawFields?: Record<string, unknown>;
  sourceNormalizedFrom?: TakeoffRow["normalizedFrom"];
  sourceWarnings?: TakeoffRow["warnings"];
  sourceConfidence?: number;
}

export interface CreateProjectMetaDraft {
  location: string;
  client: string;
  siteAddress: string;
  consultant: string;
  mainContractor: string;
}

export interface CreateProjectUnresolvedDraft {
  id: string;
  field: string;
  note: string;
  severity: "info" | "needs_review" | "important";
  apartmentTypeName: string;
  provenance?: DraftProvenance;
}

export interface AcceptedEvidenceSetDraft {
  schemaVersion: "canonical-intake-bridge.v1";
  orgId: string;
  projectId: string;
  tenderId: string;
  packId: string;
  classificationArtifactId: string;
  acceptedAt: string;
  documentIds: string[];
  adapterArtifactIds: string[];
}

export interface CreateProjectDraft {
  projectName: string;
  projectMeta: CreateProjectMetaDraft;
  requirements: CreateProjectRequirementDraft;
  requirementProvenance?: RequirementToggleProvenance;
  requirementTexts: CreateProjectRequirementTextDraft[];
  apartmentTypes: CreateProjectApartmentTypeDraft[];
  sourceApartmentMetadata?: Record<string, CreateProjectApartmentSourceMetadata>;
  unresolved: CreateProjectUnresolvedDraft[];
  sourceCoverageWarning?: SourceCoverageWarning | null;
  policyFlagSet?: SpecPolicyFlagSet | null;
  policyFlagDiscoveryUnavailable?: boolean | undefined;
  policyFlagDiscoveryFailureReason?: string | undefined;
  acceptedEvidenceSet?: AcceptedEvidenceSetDraft;
}

export type DocumentParseStatus = "queued" | "parsing" | "parsed" | "failed";

export interface DocumentToolTraceEvent {
  eventId?: string;
  eventType: string;
  message: string;
  createdAt?: string;
  tool?: string;
  argsSummary?: string;
  outcome?: string;
  elapsedMs?: number;
  pageRange?: string;
}

export interface DocumentContributionSummary {
  metaFieldCount: number;
  apartmentTypeCount: number;
  requirementCount: number;
  unresolvedCount: number;
}

export interface DocumentPageCoverage {
  pageRange: string;
  visitKind?: string;
  extractedCharCount?: number;
  notes?: readonly string[];
}

export interface SourceCoverageWarning {
  status: "partial";
  documentCount: number;
  documentTitles: readonly string[];
  readPageRanges: readonly string[];
  unreadPages: readonly number[];
  message: string;
  nextStep: string;
}

export interface ParserStatusCounts {
  total: number;
  queued: number;
  parsing: number;
  parsed: number;
  failed: number;
}

export interface UploadedDocumentDraft {
  id: string;
  title: string;
  kind: "tender" | "schedule" | "email" | "note" | "other";
  text: string;
  contentHash?: string;
  uploadFile?: File;
  uploadFingerprint?: string;
  uploadSessionReady?: boolean;
  duplicateHandling?: "replace" | "keep_both";
  characterCount: number;
  status: DocumentParseStatus;
  reviewState?: "service_unavailable";
  failureRecovery?: "retry" | "replace";
  failureKind?: "ai_malformed_response" | "service_unavailable";
  summary?: DocumentContributionSummary;
  extraction?: ProjectExtractionPayload;
  toolTrace?: DocumentToolTraceEvent[];
  tenderPackId?: string;
  tenderDocId?: string;
  parseArtifactId?: string;
  manifestArtifactId?: string;
  parseCompleteness?: "complete" | "partial" | "blocked" | "waived";
  pageCoverage?: readonly DocumentPageCoverage[];
  uncoveredPages?: readonly number[];
  verificationFlags?: readonly string[];
  policyFlagSet?: SpecPolicyFlagSet | null;
  policyFlagDiscoveryUnavailable?: boolean | undefined;
  policyFlagDiscoveryFailureReason?: string | undefined;
  error?: string;
  httpStatus?: number;
  parsedAt?: string;
}

export interface ParseDocumentResult {
  ok: boolean;
  title: string;
  alreadyUploaded?: boolean;
  serviceUnavailable?: boolean;
  summary?: DocumentContributionSummary;
}

export interface ExtractProjectResponse {
  ok: boolean;
  projectId: string;
  extraction: ProjectExtractionPayload;
  policyFlagSet?: SpecPolicyFlagSet | null;
  policyFlagDiscoveryUnavailable?: boolean | undefined;
  policyFlagDiscoveryFailureReason?: string | undefined;
}

export interface ProjectExtractionPayload {
  projectMeta?: {
    location?: string;
    client?: string;
    siteAddress?: string;
    consultant?: string;
    mainContractor?: string;
  };
  apartmentTypes?: ProjectExtractionApartmentType[];
  requirements?: ProjectExtractionRequirement[];
  unresolved?: ProjectExtractionUnresolved[];
}

export interface ProjectExtractionApartmentType {
  name: string;
  homes?: number;
  beds?: number;
  fcuZoneCount?: number;
  ufhZoneCount?: number;
  eufhZoneCount?: number;
  towelRailCount?: number;
  standaloneHeatOnlyZoneCount?: number;
  thermostatUiPointCount?: number;
  centralControlScreenCount?: number;
  hiuCount?: number;
  ciuCount?: number;
  althermaCount?: number;
  requirements?: ProjectExtractionRequirement[];
  confidence?: number;
  sourceNotes?: string[];
}

export interface ProjectExtractionRequirement {
  type?: string;
  interpretedText: string;
  priority?: Requirement["priority"];
  confidence?: number;
  scopeLabel?: string;
  scope?: {
    label?: string;
  };
  uiStrategy?: Requirement["uiStrategy"];
  edgeFeature?: Requirement["edgeFeature"];
  evidence?: string;
  sourceRefs?: SourceRef[];
}

export interface ProjectExtractionUnresolved {
  field: string;
  note: string;
  severity?: "info" | "needs_review" | "important";
  apartmentTypeName?: string;
}

const CREATED_AT = "2026-06-28T00:00:00.000Z";
export const PROJECT_NAME_MAX_LENGTH = 120;
const requirementToggleKeys = [
  "centralControlScreen",
  "leakDetection",
  "heatMetering",
  "bmsIntegration"
] as const satisfies ReadonlyArray<keyof CreateProjectRequirementDraft>;

const defaultApartmentType = (index: number): CreateProjectApartmentTypeDraft => ({
  id: `draft-type-${index}`,
  name: "",
  homes: "",
  beds: "",
  fcuZones: "",
  ufhZones: "",
  eufhZones: "",
  towelRailCount: "",
  standaloneHeatOnlyZones: "",
  thermostatUiPoints: "",
  centralScreens: "",
  hiuCount: "",
  ciuCount: "",
  althermaCount: "",
  provenance: "manual"
});

function isManualDraftItem<T extends { provenance?: DraftProvenance }>(
  item: T
): boolean {
  return item.provenance === "manual";
}

function uniqueNonEmpty(values: readonly string[]): string[] {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))];
}

export const emptyDraft = (): CreateProjectDraft => ({
  projectName: "",
  projectMeta: {
    location: "",
    client: "",
    siteAddress: "",
    consultant: "",
    mainContractor: ""
  },
  requirements: {
    centralControlScreen: false,
    leakDetection: false,
    heatMetering: false,
    bmsIntegration: false
  },
  requirementProvenance: {},
  requirementTexts: [],
  apartmentTypes: [],
  unresolved: [],
  sourceCoverageWarning: null
});

export const SOURCE_COVERAGE_NEXT_STEP =
  "Add the missing pages as pasted text or a clearer source before relying on the extracted values.";

export function documentHasPartialCoverage(
  document: Pick<
    UploadedDocumentDraft,
    "status" | "parseCompleteness" | "uncoveredPages"
  >
): boolean {
  return (
    document.status === "parsed" &&
    (document.parseCompleteness === "partial" ||
      document.parseCompleteness === "blocked" ||
      (document.uncoveredPages?.length ?? 0) > 0)
  );
}

export function sourceCoverageWarningFromDocuments(
  documents: readonly Pick<
    UploadedDocumentDraft,
    "title" | "status" | "parseCompleteness" | "pageCoverage" | "uncoveredPages"
  >[]
): SourceCoverageWarning | null {
  const limitedDocuments = documents.filter(documentHasPartialCoverage);

  if (limitedDocuments.length === 0) {
    return null;
  }

  const readPageRanges = uniqueNonEmpty(
    limitedDocuments.flatMap((document) =>
      (document.pageCoverage ?? []).map((coverage) => coverage.pageRange)
    )
  );
  const unreadPages = [
    ...new Set(
      limitedDocuments
        .flatMap((document) => document.uncoveredPages ?? [])
        .filter((page) => Number.isInteger(page) && page > 0)
    )
  ].sort((left, right) => left - right);
  const documentTitles = limitedDocuments.map((document) => document.title);

  return {
    status: "partial",
    documentCount: limitedDocuments.length,
    documentTitles,
    readPageRanges,
    unreadPages,
    message: `Partial parse: ${limitedDocuments.length} source${
      limitedDocuments.length === 1 ? "" : "s"
    } had limits while reading. Read pages: ${
      readPageRanges.length > 0 ? readPageRanges.join(", ") : "not reported"
    }. Unread pages: ${
      unreadPages.length > 0 ? unreadPages.join(", ") : "not reported"
    }.`,
    nextStep: SOURCE_COVERAGE_NEXT_STEP
  };
}

export function aggregateDocumentContributionSummaries(
  summaries: readonly DocumentContributionSummary[]
): DocumentContributionSummary {
  return summaries.reduce(
    (total, summary) => ({
      metaFieldCount: total.metaFieldCount + summary.metaFieldCount,
      apartmentTypeCount: total.apartmentTypeCount + summary.apartmentTypeCount,
      requirementCount: total.requirementCount + summary.requirementCount,
      unresolvedCount: total.unresolvedCount + summary.unresolvedCount
    }),
    {
      metaFieldCount: 0,
      apartmentTypeCount: 0,
      requirementCount: 0,
      unresolvedCount: 0
    }
  );
}

export const buildSampleCreateProjectDraft = (): CreateProjectDraft => ({
  projectName: "Riverside Court",
  projectMeta: {
    location: "Wandsworth, London",
    client: "Example Developments",
    siteAddress: "Riverside Court, Wandsworth",
    consultant: "",
    mainContractor: ""
  },
  requirements: {
    centralControlScreen: true,
    leakDetection: true,
    heatMetering: true,
    bmsIntegration: true
  },
  requirementProvenance: {
    centralControlScreen: "manual",
    leakDetection: "manual",
    heatMetering: "manual",
    bmsIntegration: "manual"
  },
  requirementTexts: [],
  apartmentTypes: [
    {
      id: "draft-type-1",
      name: "Type A - two bedroom",
      homes: "12",
      beds: "2",
      fcuZones: "3",
      ufhZones: "2",
      eufhZones: "0",
      towelRailCount: "0",
      standaloneHeatOnlyZones: "0",
      thermostatUiPoints: "",
      centralScreens: "1",
      hiuCount: "1",
      ciuCount: "0",
      althermaCount: "0"
    },
    {
      id: "draft-type-2",
      name: "Type B - three bedroom",
      homes: "8",
      beds: "3",
      fcuZones: "4",
      ufhZones: "3",
      eufhZones: "0",
      towelRailCount: "0",
      standaloneHeatOnlyZones: "0",
      thermostatUiPoints: "",
      centralScreens: "1",
      hiuCount: "1",
      ciuCount: "0",
      althermaCount: "0"
    }
  ],
  unresolved: []
});

const APARTMENT_MATRIX_UNRESOLVED_FIELD_PATTERN =
  /\b(apartment\s*(?:type|types|schedule|matrix|row|rows)|dwelling|dwellings|homes|unit\s*(?:type|types|count|counts)|fcu|fan\s*coil|ufh|underfloor|eufh|towel\s*rail|heat[-\s]*only|zone\s*(?:count|counts)|bed\s*(?:count|counts|rooms?)|central\s*(?:screen|screens)|hiu|ciu|altherma)\b/i;

function unresolvedIsSupersededByApartmentMatrix(
  unresolved: ProjectExtractionUnresolved,
  apartmentTypes: readonly CreateProjectApartmentTypeDraft[]
): boolean {
  if (apartmentTypes.length === 0) {
    return false;
  }

  const field = `${unresolved.field} ${unresolved.apartmentTypeName ?? ""}`;

  return APARTMENT_MATRIX_UNRESOLVED_FIELD_PATTERN.test(field);
}

export const buildCreateProjectDraftFromExtraction = (
  current: CreateProjectDraft,
  extraction: ProjectExtractionPayload
): CreateProjectDraft => {
  const projectRequirements = extraction.requirements ?? [];
  const apartmentTypes = (extraction.apartmentTypes ?? []).map(
    (apartmentType, index) => ({
      id: `draft-type-${index + 1}-${slug(apartmentType.name)}`,
      name: apartmentType.name,
      homes: numberField(apartmentType.homes),
      beds: numberField(apartmentType.beds),
      fcuZones: numberField(apartmentType.fcuZoneCount),
      ufhZones: numberField(apartmentType.ufhZoneCount),
      eufhZones: numberField(apartmentType.eufhZoneCount),
      towelRailCount: numberField(apartmentType.towelRailCount),
      standaloneHeatOnlyZones: numberField(apartmentType.standaloneHeatOnlyZoneCount),
      thermostatUiPoints: numberField(apartmentType.thermostatUiPointCount),
      centralScreens: numberField(apartmentType.centralControlScreenCount),
      hiuCount: numberField(apartmentType.hiuCount),
      ciuCount: numberField(apartmentType.ciuCount),
      althermaCount: numberField(apartmentType.althermaCount),
      provenance: "source" as const
    })
  );
  const scopedRequirements = (extraction.apartmentTypes ?? []).flatMap(
    (apartmentType) =>
      (apartmentType.requirements ?? []).map((requirement) => ({
        ...requirement,
        scopeLabel:
          requirement.scopeLabel ?? requirement.scope?.label ?? apartmentType.name
      }))
  );
  const requirementTexts = uniqueRequirementDrafts([
    ...projectRequirements,
    ...scopedRequirements
  ]);
  const unresolved = (extraction.unresolved ?? [])
    .filter((item) => !unresolvedIsSupersededByApartmentMatrix(item, apartmentTypes))
    .map((item, index) => ({
      id: `unresolved-${index + 1}-${slug(item.field)}`,
      field: item.field,
      note: item.note,
      severity: item.severity ?? "needs_review",
      apartmentTypeName: item.apartmentTypeName ?? "",
      provenance: "source" as const
    }));

  if (apartmentTypes.length === 0) {
    unresolved.push({
      id: "unresolved-apartment-types",
      field: "apartment types",
      note: "No apartment schedule was extracted. Add a type or paste a schedule before specifying controls.",
      severity: "important",
      apartmentTypeName: "",
      provenance: "source"
    });
  }

  const sourceRequirementToggles = deriveRequirementToggles(
    requirementTexts,
    apartmentTypes
  );
  const manualRequirementProvenance = current.requirementProvenance ?? {};
  const nextRequirements = { ...sourceRequirementToggles };
  const nextRequirementProvenance: RequirementToggleProvenance = {};

  for (const key of requirementToggleKeys) {
    if (manualRequirementProvenance[key] === "manual") {
      nextRequirements[key] = current.requirements[key];
      nextRequirementProvenance[key] = "manual";
      continue;
    }

    if (sourceRequirementToggles[key]) {
      nextRequirementProvenance[key] = "source";
    }
  }

  return {
    ...current,
    projectMeta: {
      location: extraction.projectMeta?.location ?? current.projectMeta.location,
      client: extraction.projectMeta?.client ?? current.projectMeta.client,
      siteAddress:
        extraction.projectMeta?.siteAddress ?? current.projectMeta.siteAddress,
      consultant: extraction.projectMeta?.consultant ?? current.projectMeta.consultant,
      mainContractor:
        extraction.projectMeta?.mainContractor ?? current.projectMeta.mainContractor
    },
    requirements: nextRequirements,
    requirementProvenance: nextRequirementProvenance,
    requirementTexts: [
      ...requirementTexts,
      ...current.requirementTexts.filter(isManualDraftItem)
    ],
    apartmentTypes: [
      ...apartmentTypes,
      ...current.apartmentTypes.filter(isManualDraftItem)
    ],
    unresolved: [...unresolved, ...current.unresolved.filter(isManualDraftItem)]
  };
};

export const buildSpecifyProjectInputFromDraft = (
  draft: CreateProjectDraft
): SpecifyProjectInput => {
  const projectName = draft.projectName.trim();

  if (!projectName) {
    throw new Error("Enter a project name before specifying controls.");
  }

  if (projectName.length > PROJECT_NAME_MAX_LENGTH) {
    throw new Error(
      `Project names must be ${PROJECT_NAME_MAX_LENGTH} characters or fewer before specifying controls.`
    );
  }

  const specReadyApartmentTypes = draft.apartmentTypes.filter(
    hasMinimumApartmentTypeData
  );

  if (specReadyApartmentTypes.length === 0) {
    throw new Error(
      "Add at least one apartment matrix row with a type name, homes/dwellings count, and at least one FCU, UFH, towel rail, or heat-only zone before specifying controls."
    );
  }

  const projectId = `created-${slug(projectName)}`;
  const requirements = buildRequirements({
    projectId,
    projectName,
    draft
  });
  const apartmentTypes = specReadyApartmentTypes.map((apartmentType, index) => {
    const name = apartmentType.name.trim();

    if (!name) {
      throw new Error(`Apartment type ${index + 1} needs a type name.`);
    }

    const id = `${projectId}-${slug(name)}-${index + 1}`;
    const homes = positiveInteger(apartmentType.homes, `${name} homes`);
    const beds = nonNegativeInteger(apartmentType.beds, `${name} bedrooms`);
    const fcuZones = nonNegativeInteger(
      apartmentType.fcuZones,
      `${name} fan-coil zones`
    );
    const ufhZones = nonNegativeInteger(
      apartmentType.ufhZones,
      `${name} underfloor heating zones`
    );
    const wetActuatorCount =
      apartmentType.wetActuatorCount === undefined ||
      apartmentType.wetActuatorCount.trim() === ""
        ? ufhZones
        : nonNegativeInteger(
            apartmentType.wetActuatorCount,
            `${name} wet UFH actuators`
          );
    const eufhZones = nonNegativeInteger(
      apartmentType.eufhZones,
      `${name} electric underfloor heating zones`
    );
    const towelRailCount = nonNegativeInteger(
      apartmentType.towelRailCount,
      `${name} towel rail zones`
    );
    const standaloneHeatOnlyZones = nonNegativeInteger(
      apartmentType.standaloneHeatOnlyZones,
      `${name} heat-only zones`
    );
    const thermostatUiPoints = optionalNonNegativeInteger(
      apartmentType.thermostatUiPoints,
      `${name} thermostat/control points`
    );
    const centralScreens = optionalNonNegativeInteger(
      apartmentType.centralScreens,
      `${name} central screens`
    );
    const hiuCount = nonNegativeInteger(apartmentType.hiuCount, `${name} HIUs`);
    const ciuCount = nonNegativeInteger(apartmentType.ciuCount, `${name} CIUs`);
    const althermaCount = nonNegativeInteger(
      apartmentType.althermaCount,
      `${name} Altherma units`
    );
    const zoneCount =
      fcuZones + ufhZones + eufhZones + towelRailCount + standaloneHeatOnlyZones;

    if (zoneCount === 0) {
      throw new Error(
        `${name} needs at least one extracted or entered FCU, UFH, towel rail, or heat-only control zone.`
      );
    }

    const sourceMetadata = draft.sourceApartmentMetadata?.[apartmentType.id];
    const takeoffRow = buildTakeoffRow({
      projectId,
      apartmentTypeId: id,
      name,
      homes,
      beds,
      fcuZones,
      ufhZones,
      wetActuatorCount,
      eufhZones,
      towelRailCount,
      standaloneHeatOnlyZones,
      thermostatUiPoints,
      centralScreens,
      hiuCount,
      ciuCount,
      althermaCount,
      requirements: draft.requirements,
      meta: draft.projectMeta,
      ...(sourceMetadata?.sourceRefs?.length
        ? { sourceRefs: sourceMetadata.sourceRefs }
        : {}),
      ...(sourceMetadata?.sourceTakeoffArtifactId
        ? { sourceTakeoffArtifactId: sourceMetadata.sourceTakeoffArtifactId }
        : {}),
      ...(sourceMetadata?.sourceRawFields
        ? { sourceRawFields: sourceMetadata.sourceRawFields }
        : {}),
      ...(sourceMetadata?.sourceNormalizedFrom
        ? { sourceNormalizedFrom: sourceMetadata.sourceNormalizedFrom }
        : {}),
      ...(sourceMetadata?.sourceWarnings
        ? { sourceWarnings: sourceMetadata.sourceWarnings }
        : {}),
      ...(sourceMetadata?.sourceConfidence !== undefined
        ? { sourceConfidence: sourceMetadata.sourceConfidence }
        : {})
    });

    return {
      id,
      name,
      homes,
      takeoffRows: [takeoffRow],
      requirements
    };
  });

  return {
    projectId,
    projectName,
    apartmentTypes,
    ...(draft.policyFlagSet ? { policyFlagSet: draft.policyFlagSet } : {})
  };
};

export function buildTakeoffRow(input: {
  projectId: string;
  apartmentTypeId: string;
  name: string;
  homes: number;
  beds: number;
  fcuZones: number;
  ufhZones: number;
  wetActuatorCount?: number;
  eufhZones: number;
  towelRailCount: number;
  standaloneHeatOnlyZones: number;
  thermostatUiPoints: number | undefined;
  centralScreens: number | undefined;
  hiuCount: number;
  ciuCount: number;
  althermaCount: number;
  requirements: CreateProjectRequirementDraft;
  meta: CreateProjectMetaDraft;
  sourceRefs?: SourceRef[];
  sourceTakeoffArtifactId?: string;
  sourceRawFields?: Record<string, unknown>;
  sourceNormalizedFrom?: TakeoffRow["normalizedFrom"];
  sourceWarnings?: TakeoffRow["warnings"];
  sourceConfidence?: number;
}): TakeoffRow {
  const sourceRef = manualSourceRef({
    projectId: input.projectId,
    sourceId: `${input.apartmentTypeId}-takeoff`,
    locator: `Create project extraction review / ${input.name}`
  });
  const thermostatUiPointCount =
    input.thermostatUiPoints ??
    input.fcuZones + input.ufhZones + input.eufhZones + input.standaloneHeatOnlyZones;
  const centralControlScreenCount =
    input.centralScreens ?? (input.requirements.centralControlScreen ? 1 : undefined);
  const engineSafeSourceProjection = EngineSafeTakeoffProjectionSchema.safeParse(
    input.sourceRawFields?.[ENGINE_SAFE_TAKEOFF_RAW_FIELDS_KEY]
  );
  const reviewedEngineSafeProjection = engineSafeSourceProjection.success
    ? projectTakeoffForEngine({
        apartmentTypeId: input.apartmentTypeId,
        quantity: input.homes,
        beds: input.beds,
        fcuCount: input.fcuZones,
        towelRailCount: input.towelRailCount,
        wufhZoneCount: input.ufhZones,
        wetActuatorCount: input.wetActuatorCount ?? input.ufhZones,
        eufhZoneCount: input.eufhZones,
        standaloneHeatOnlyZoneCount: input.standaloneHeatOnlyZones,
        thermostatUiPointCount,
        ...(centralControlScreenCount !== undefined
          ? { centralControlScreenCount }
          : {}),
        hiuCount: input.hiuCount,
        ciuCount: input.ciuCount,
        althermaCount: input.althermaCount,
        rawFields: {
          [ENGINE_SAFE_TAKEOFF_RAW_FIELDS_KEY]: engineSafeSourceProjection.data
        }
      })
    : undefined;
  const rawFields: TakeoffRow["rawFields"] = engineSafeSourceProjection.success
    ? {
        [ENGINE_SAFE_TAKEOFF_RAW_FIELDS_KEY]: reviewedEngineSafeProjection
      }
    : {
        Project: input.projectId,
        "Apartment type": input.name,
        Homes: input.homes,
        Bedrooms: input.beds,
        "Fan-coil zones": input.fcuZones,
        "Wet underfloor heating zones": input.ufhZones,
        "Wet UFH actuators": input.wetActuatorCount ?? input.ufhZones,
        "Electric underfloor heating zones": input.eufhZones,
        "Towel rail zones": input.towelRailCount,
        "Heat-only zones": input.standaloneHeatOnlyZones,
        "Thermostat/control points": thermostatUiPointCount,
        "Central screens": centralControlScreenCount ?? 0,
        HIUs: input.hiuCount,
        CIUs: input.ciuCount,
        Altherma: input.althermaCount,
        Location: input.meta.location,
        Client: input.meta.client,
        "Central screen requirement": input.requirements.centralControlScreen,
        "Leak detection Edge": input.requirements.leakDetection,
        "Heat metering": input.requirements.heatMetering,
        "BMS integration": input.requirements.bmsIntegration,
        ...(input.sourceTakeoffArtifactId
          ? { tenderTakeoffArtifactId: input.sourceTakeoffArtifactId }
          : {}),
        ...(input.sourceRawFields ? input.sourceRawFields : {})
      };

  return {
    apartmentTypeId: input.apartmentTypeId,
    rawLabel: input.name,
    quantity: input.homes,
    ...(input.beds > 0 ? { beds: input.beds } : {}),
    fcuCount: input.fcuZones,
    towelRailCount: input.towelRailCount,
    wufhZoneCount: input.ufhZones,
    wetActuatorCount: input.wetActuatorCount ?? input.ufhZones,
    eufhZoneCount: input.eufhZones,
    standaloneHeatOnlyZoneCount: input.standaloneHeatOnlyZones,
    thermostatUiPointCount,
    ...(centralControlScreenCount !== undefined ? { centralControlScreenCount } : {}),
    hiuCount: input.hiuCount,
    ciuCount: input.ciuCount,
    althermaCount: input.althermaCount,
    sourceRefs: input.sourceRefs?.length ? input.sourceRefs : [sourceRef],
    rawFields,
    normalizedFrom: input.sourceNormalizedFrom ?? [
      {
        rawHeading: "Fan-coil zones",
        canonicalField: "fcuCount",
        confidence: 0.92
      },
      {
        rawHeading: "Wet underfloor heating zones",
        canonicalField: "wufhZoneCount",
        confidence: 0.9
      },
      {
        rawHeading: "Number of homes",
        canonicalField: "quantity",
        confidence: 0.92
      },
      {
        rawHeading: "Central control screen per apartment",
        canonicalField: "centralControlScreenCount",
        confidence: 0.86
      }
    ],
    confidence: input.sourceConfidence ?? 0.86,
    warnings: input.sourceWarnings ?? []
  };
}

export function buildRequirements(input: {
  projectId: string;
  projectName: string;
  draft: CreateProjectDraft;
}): Requirement[] {
  const requirements: Requirement[] = [];
  const seenTypes = new Set<string>();

  for (const [index, item] of input.draft.requirementTexts.entries()) {
    const interpretedText = item.interpretedText.trim();

    if (!interpretedText) {
      continue;
    }

    const type = canonicalRequirementType(item.type);
    seenTypes.add(type);
    requirements.push(
      requirement({
        projectId: input.projectId,
        projectName: input.projectName,
        id: `${input.projectId}-extracted-${index + 1}-${slug(type)}`,
        type,
        interpretedText,
        priority: item.priority,
        confidence: optionalConfidence(item.confidence) ?? 0.82,
        ...(item.uiStrategy ? { uiStrategy: item.uiStrategy } : {}),
        ...(item.edgeFeature ? { edgeFeature: item.edgeFeature } : {}),
        createdBy:
          item.sourceNoteAuthorType === "user"
            ? "human"
            : item.sourceNoteAuthorType === "ai" || item.provenance === "source"
              ? "agent"
              : "human",
        locator: `Create project extraction review / requirement / ${type}`,
        ...(item.sourceNoteId
          ? {
              sourceRef: noteSourceRef({
                projectId: input.projectId,
                sourceNoteId: item.sourceNoteId,
                ...(item.sourceNoteLabel
                  ? { sourceNoteLabel: item.sourceNoteLabel }
                  : {})
              })
            }
          : {}),
        ...(item.sourceRefs?.length ? { sourceRefs: item.sourceRefs } : {})
      })
    );
  }

  if (
    input.draft.requirements.centralControlScreen &&
    !seenTypes.has("central_control_screen")
  ) {
    requirements.push(
      requirement({
        projectId: input.projectId,
        projectName: input.projectName,
        id: `${input.projectId}-central-screen`,
        type: "central_control_screen",
        interpretedText:
          "Provide one central apartment control screen per home as the master user interface, with local room control for each heating and cooling zone.",
        uiStrategy: "per_room_plus_central"
      })
    );
  }

  if (input.draft.requirements.leakDetection && !seenTypes.has("edge_leak_detection")) {
    requirements.push(
      requirement({
        projectId: input.projectId,
        projectName: input.projectName,
        id: `${input.projectId}-edge-leak-detection`,
        type: "edge_leak_detection",
        interpretedText:
          "Edge leak detection is required: bridge the existing Titan BACnet network to dashboard and alert workflows for each apartment.",
        priority: "blocking",
        edgeFeature: {
          featureKind: "leak_detection",
          provisionUnit: "per_apartment",
          inputPolicy: "reuse_existing_titan_inputs",
          requiresCloudDashboard: true,
          requiresSmsNotification: true
        }
      })
    );
  }

  if (input.draft.requirements.heatMetering && !seenTypes.has("metering")) {
    requirements.push(
      requirement({
        projectId: input.projectId,
        projectName: input.projectName,
        id: `${input.projectId}-heat-metering`,
        type: "metering",
        interpretedText:
          "Apartment heat metering is required via Modbus RTU RS485. The TP-NM-R-EM-D NetMaster is the Modbus master and reads by-others heat meters."
      })
    );
  }

  if (input.draft.requirements.bmsIntegration && !seenTypes.has("bms_integration")) {
    requirements.push(
      requirement({
        projectId: input.projectId,
        projectName: input.projectName,
        id: `${input.projectId}-bms-integration`,
        type: "bms_integration",
        interpretedText:
          "Expose apartment controls, alarms and metering values where present to the landlord BMS over BACnet/IP through the NetMaster."
      })
    );
  }

  return requirements;
}

function requirement(input: {
  projectId: string;
  projectName: string;
  id: string;
  type: string;
  interpretedText: string;
  priority?: Requirement["priority"];
  uiStrategy?: Requirement["uiStrategy"];
  edgeFeature?: Requirement["edgeFeature"];
  confidence?: number;
  createdBy?: Requirement["createdBy"];
  locator?: string;
  sourceRef?: SourceRef;
  sourceRefs?: SourceRef[];
}): Requirement {
  return {
    id: input.id,
    projectId: input.projectId,
    scope: {
      level: "project",
      refId: input.projectId,
      label: input.projectName
    },
    type: input.type,
    ...(input.uiStrategy ? { uiStrategy: input.uiStrategy } : {}),
    ...(input.edgeFeature ? { edgeFeature: input.edgeFeature } : {}),
    interpretedText: input.interpretedText,
    priority: input.priority ?? "default",
    sourceRefs: input.sourceRefs?.length
      ? input.sourceRefs
      : input.sourceRef
        ? [input.sourceRef]
        : [
            manualSourceRef({
              projectId: input.projectId,
              sourceId: input.id,
              locator:
                input.locator ?? `Create project extraction review / ${input.type}`
            })
          ],
    status: "approved",
    confidence: input.confidence ?? 0.9,
    overrideState: "none",
    interactsWith: [],
    canSubstituteFor: [],
    createdBy: input.createdBy ?? "human",
    createdAt: CREATED_AT
  };
}

function noteSourceRef(input: {
  projectId: string;
  sourceNoteId: string;
  sourceNoteLabel?: string;
}): SourceRef {
  const label = input.sourceNoteLabel?.trim() || input.sourceNoteId;

  return {
    sourceId: input.sourceNoteId,
    sourceKind: "note",
    authorityClass: "baseline_note",
    locator: `Project notes / promoted note / ${label} (${input.projectId})`
  };
}

function manualSourceRef(input: {
  projectId: string;
  sourceId: string;
  locator: string;
}): SourceRef {
  return {
    sourceId: input.sourceId,
    sourceKind: "manual_entry",
    authorityClass: "takeoff",
    locator: `${input.locator} (${input.projectId})`
  };
}

export function combineProjectExtractions(
  extractions: readonly ProjectExtractionPayload[]
): ProjectExtractionPayload {
  const combinedMeta: NonNullable<ProjectExtractionPayload["projectMeta"]> = {};
  const apartmentTypes: ProjectExtractionApartmentType[] = [];
  const requirements: ProjectExtractionRequirement[] = [];
  const unresolved: ProjectExtractionUnresolved[] = [];

  for (const extraction of extractions) {
    mergeMetaField(combinedMeta, "location", extraction.projectMeta?.location);
    mergeMetaField(combinedMeta, "client", extraction.projectMeta?.client);
    mergeMetaField(combinedMeta, "siteAddress", extraction.projectMeta?.siteAddress);
    mergeMetaField(combinedMeta, "consultant", extraction.projectMeta?.consultant);
    mergeMetaField(
      combinedMeta,
      "mainContractor",
      extraction.projectMeta?.mainContractor
    );
    apartmentTypes.push(...(extraction.apartmentTypes ?? []));
    requirements.push(...(extraction.requirements ?? []));
    unresolved.push(...(extraction.unresolved ?? []));
  }

  return {
    projectMeta: combinedMeta,
    apartmentTypes: mergeApartmentTypesByName(apartmentTypes),
    requirements,
    unresolved
  };
}

function mergeMetaField(
  meta: NonNullable<ProjectExtractionPayload["projectMeta"]>,
  field: keyof NonNullable<ProjectExtractionPayload["projectMeta"]>,
  value: string | undefined
) {
  if (!meta[field] && value?.trim()) {
    meta[field] = value.trim();
  }
}

const apartmentNumericFields = [
  "homes",
  "beds",
  "fcuZoneCount",
  "ufhZoneCount",
  "eufhZoneCount",
  "towelRailCount",
  "standaloneHeatOnlyZoneCount",
  "thermostatUiPointCount",
  "centralControlScreenCount",
  "hiuCount",
  "ciuCount",
  "althermaCount"
] as const satisfies ReadonlyArray<keyof ProjectExtractionApartmentType>;

function mergeApartmentTypesByName(
  apartmentTypes: readonly ProjectExtractionApartmentType[]
): ProjectExtractionApartmentType[] {
  const merged = new Map<string, ProjectExtractionApartmentType>();

  for (const apartmentType of apartmentTypes) {
    // Normalise to the type CODE so name variants for the SAME physical type
    // ("Type A1 (Studio)" == "A1" == "A1 Studio") collapse into one row,
    // instead of slug() treating each spelling as a separate apartment type.
    const key = normaliseApartmentTypeKey(apartmentType.name);
    const current = merged.get(key);

    if (!current) {
      merged.set(key, {
        ...apartmentType,
        requirements: [...(apartmentType.requirements ?? [])],
        sourceNotes: [...(apartmentType.sourceNotes ?? [])]
      });
      continue;
    }

    // Keep the more descriptive name when variants merge.
    current.name = richerExtractionName(current.name, apartmentType.name);

    for (const field of apartmentNumericFields) {
      if (current[field] === undefined && apartmentType[field] !== undefined) {
        current[field] = apartmentType[field] as never;
      }
    }

    current.requirements = [
      ...(current.requirements ?? []),
      ...(apartmentType.requirements ?? [])
    ];
    current.sourceNotes = [
      ...(current.sourceNotes ?? []),
      ...(apartmentType.sourceNotes ?? [])
    ];
  }

  return [...merged.values()];
}

function richerExtractionName(a: string, b: string): string {
  const aTrim = (a ?? "").trim();
  const bTrim = (b ?? "").trim();

  if (!aTrim) {
    return bTrim;
  }
  if (!bTrim) {
    return aTrim;
  }

  const aHasParen = /\(/.test(aTrim);
  const bHasParen = /\(/.test(bTrim);

  if (aHasParen !== bHasParen) {
    return aHasParen ? aTrim : bTrim;
  }

  return bTrim.length > aTrim.length ? bTrim : aTrim;
}

export function scheduleRecordsToApartmentDrafts(
  records: readonly Record<string, unknown>[],
  sourceSlug: string
): CreateProjectApartmentTypeDraft[] {
  return records.flatMap((record, index) => {
    const draft = defaultApartmentType(index + 1);
    const next: CreateProjectApartmentTypeDraft = {
      ...draft,
      id: `schedule-${sourceSlug}-${index + 1}`,
      name: scheduleValue(record, ["type", "apartment type", "unit type", "layout"]),
      homes: scheduleNumberValue(record, [
        "homes",
        "dwellings",
        "apartments",
        "quantity",
        "qty",
        "count"
      ]),
      beds: scheduleNumberValue(record, ["beds", "bedrooms", "bed"]),
      fcuZones: scheduleNumberValue(record, [
        "fcu zones",
        "fcu",
        "fan coil",
        "fan-coil",
        "fan coil zones"
      ]),
      ufhZones: scheduleNumberValue(record, [
        "wet ufh",
        "ufh",
        "ufh zones",
        "wet underfloor heating"
      ]),
      eufhZones: scheduleNumberValue(record, [
        "electric ufh",
        "eufh",
        "electric underfloor heating"
      ]),
      towelRailCount: scheduleNumberValue(record, ["towel rails", "towel rail"]),
      standaloneHeatOnlyZones: scheduleNumberValue(record, [
        "heat-only zones",
        "heat only zones",
        "heat only"
      ]),
      thermostatUiPoints: scheduleNumberValue(record, [
        "control points",
        "thermostats",
        "ui points"
      ]),
      centralScreens: scheduleNumberValue(record, [
        "central screens",
        "central screen",
        "screens"
      ]),
      hiuCount: scheduleNumberValue(record, ["hiu", "hius"]),
      ciuCount: scheduleNumberValue(record, ["ciu", "cius"]),
      althermaCount: scheduleNumberValue(record, ["altherma", "altherma units"])
    };
    const hasContent = Object.entries(next).some(
      ([field, value]) => field !== "id" && value.trim()
    );

    return hasContent ? [next] : [];
  });
}

function scheduleValue(
  record: Record<string, unknown>,
  aliases: readonly string[]
): string {
  const normalisedAliases = new Set(aliases.map(normaliseScheduleHeader));

  for (const [key, value] of Object.entries(record)) {
    if (!normalisedAliases.has(normaliseScheduleHeader(key))) {
      continue;
    }

    return String(value ?? "").trim();
  }

  return "";
}

function scheduleNumberValue(
  record: Record<string, unknown>,
  aliases: readonly string[]
): string {
  const raw = scheduleValue(record, aliases);
  const trimmed = raw.replace(/,/g, "").trim();

  return /^\d+$/.test(trimmed) ? trimmed : raw.trim();
}

function normaliseScheduleHeader(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

export function uniqueRequirementDrafts(
  requirements: readonly ProjectExtractionRequirement[]
): CreateProjectRequirementTextDraft[] {
  const seen = new Set<string>();
  const drafts: CreateProjectRequirementTextDraft[] = [];

  for (const requirementItem of requirements) {
    const interpretedText = requirementItem.interpretedText?.trim();

    if (!interpretedText) {
      continue;
    }

    const type = canonicalRequirementType(
      requirementItem.type ?? "requirement_amendment"
    );
    const key = `${type}:${interpretedText.toLowerCase()}`;

    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    drafts.push({
      id: `requirement-${drafts.length + 1}-${slug(type)}`,
      type,
      interpretedText,
      priority: requirementItem.priority ?? "default",
      confidence: numberField(requirementItem.confidence),
      scopeLabel: requirementItem.scopeLabel ?? requirementItem.scope?.label ?? "",
      ...(requirementItem.uiStrategy ? { uiStrategy: requirementItem.uiStrategy } : {}),
      ...(requirementItem.edgeFeature
        ? { edgeFeature: requirementItem.edgeFeature }
        : {}),
      ...(requirementItem.sourceRefs?.length
        ? { sourceRefs: requirementItem.sourceRefs }
        : {}),
      provenance: "source"
    });
  }

  return drafts;
}

export function deriveRequirementToggles(
  requirements: readonly CreateProjectRequirementTextDraft[],
  apartmentTypes: readonly CreateProjectApartmentTypeDraft[]
): CreateProjectRequirementDraft {
  const requirementText = requirements
    .map(
      (requirementItem) => `${requirementItem.type} ${requirementItem.interpretedText}`
    )
    .join("\n")
    .toLowerCase();

  return {
    centralControlScreen:
      apartmentTypes.some(
        (apartmentType) => Number(apartmentType.centralScreens) > 0
      ) ||
      includesAny(requirementText, [
        "central_control_screen",
        "central control screen"
      ]),
    leakDetection: includesAny(requirementText, [
      "edge_leak_detection",
      "leak detection",
      "leak alarm",
      "sms alert"
    ]),
    heatMetering: includesAny(requirementText, [
      "metering",
      "heat meter",
      "utility meter",
      "m-bus",
      "modbus",
      "pulse"
    ]),
    bmsIntegration: includesAny(requirementText, [
      "bms",
      "bacnet",
      "landlord",
      "head-end",
      "head end"
    ])
  };
}

function canonicalRequirementType(type: string): string {
  const normalised = type
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_");

  if (normalised.includes("leak")) {
    return "edge_leak_detection";
  }

  if (normalised.includes("meter")) {
    return "metering";
  }

  if (normalised.includes("bms") || normalised.includes("bacnet")) {
    return "bms_integration";
  }

  if (normalised.includes("central") && normalised.includes("screen")) {
    return "central_control_screen";
  }

  return normalised || "requirement_amendment";
}

export function positiveInteger(value: string, label: string): number {
  const parsed = Number(value);

  if (!Number.isInteger(parsed) || parsed <= 0) {
    throw new Error(`${label} must be a whole number greater than zero.`);
  }

  return parsed;
}

export function hasMinimumApartmentTypeData(
  apartmentType: CreateProjectApartmentTypeDraft
): boolean {
  if (!apartmentType.name.trim()) {
    return false;
  }

  try {
    positiveInteger(apartmentType.homes, "homes");
    const zoneCount =
      nonNegativeInteger(apartmentType.fcuZones, "FCU zones") +
      nonNegativeInteger(apartmentType.ufhZones, "wet UFH zones") +
      nonNegativeInteger(apartmentType.eufhZones, "electric UFH zones") +
      nonNegativeInteger(apartmentType.towelRailCount, "towel rail zones") +
      nonNegativeInteger(apartmentType.standaloneHeatOnlyZones, "heat-only zones");

    return zoneCount > 0;
  } catch {
    return false;
  }
}

export function nonNegativeInteger(value: string, label: string): number {
  const parsed = value.trim() === "" ? 0 : Number(value);

  if (!Number.isInteger(parsed) || parsed < 0) {
    throw new Error(`${label} must be a whole number of zero or more.`);
  }

  return parsed;
}

export function optionalNonNegativeInteger(
  value: string,
  label: string
): number | undefined {
  if (!value.trim()) {
    return undefined;
  }

  return nonNegativeInteger(value, label);
}

export function optionalConfidence(value: string): number | undefined {
  if (!value.trim()) {
    return undefined;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : undefined;
}

export function numberField(value: number | undefined): string {
  return value === undefined ? "" : String(value);
}

function includesAny(value: string, needles: readonly string[]): boolean {
  return needles.some((needle) => value.includes(needle));
}

export function slug(value: string): string {
  const slugged = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  return slugged || "project";
}
